import React from 'react';
import { getCurrencySymbolFromCode, useIntl } from '@futrli/localisation';
import clsx from 'clsx';
import { addYears, format, subYears } from 'date-fns';
import { Option, Select } from 'carbon-react/lib/components/select';
import classNames from 'classnames';
import DateInput, { DateChangeEvent } from 'carbon-react/lib/components/date';

import { UICarbonNumberField } from '@futrli/ui-components';

import {
  MAX_FUTURE_YEARS_FOR_ASSUMPTIONS,
  MAX_PERCENTAGE,
  MIN_PREV_YEARS_FOR_ASSUMPTIONS
} from '../../../../core/constants';
import { DateState, OwnState, PriceVariant, StringState } from './types';
import styles from './unit.scss';
import { getNextDay } from './helpers';
import { forceLocalDate } from '../../../../utils/dates';

interface Props {
  price_variant: PriceVariant;
  index: number;
  currency?: string;
  missing_end_date_for_last_variant: boolean;
  clearable: boolean;
  handleOnNumberChange: (name: keyof StringState, index: number) => (value: string) => void;
  hasError: (fieldName: keyof OwnState, index?: number, subFieldName?: keyof PriceVariant) => boolean;
  handleOnBlur: (name: keyof StringState, index: number) => (value: string) => void;
  handleDateChange: (name: keyof DateState, index: number) => (date: Date) => void;
  handleDateClear: (name: keyof DateState, index: number) => () => void;
  handleOnChange: (
    name: keyof StringState
  ) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

const MAX_DIGITS_FOR_QUANTITY = 6;
const MAX_DIGITS_FOR_SELLING_PRICE = 10;

export const PriceVariantComponent = ({
  price_variant,
  index,
  currency,
  missing_end_date_for_last_variant,
  handleOnNumberChange,
  hasError,
  handleOnBlur,
  handleDateChange,
  handleOnChange
}: Props) => {
  const MAX_DATE = addYears(Date.now(), MAX_FUTURE_YEARS_FOR_ASSUMPTIONS);
  const MIN_DATE = subYears(Date.now(), MIN_PREV_YEARS_FOR_ASSUMPTIONS);
  const currencySymbol = getCurrencySymbolFromCode(currency!);
  const intl = useIntl();

  const handleCarbonStartDateChange = (e: DateChangeEvent) =>
    handleDateChange('start_date', index)(forceLocalDate(e.target.value.rawValue || ''));

  const handleCarbonEndDateChange = (e: DateChangeEvent) =>
    handleDateChange('end_date', index)(forceLocalDate(e.target.value.rawValue || ''));

  return (
    <div key={index} className={styles.formContainer} data-testid="price-variant-section">
      <div className={clsx(styles.formWrapper, styles.flowChartCarbon)}>
        <div className={styles.formColumn}>
          <div className={styles.formItemContainer}>
            <UICarbonNumberField
              label={intl.formatMessage({ id: 'predict.unit_price', defaultMessage: 'Unit Price' })}
              data-testid={`selling_price_${index}`}
              autoComplete="off"
              type="currency"
              currencySymbol={currencySymbol}
              className={styles.isNarrowCarbon}
              style={{ marginRight: 16 }}
              maxLength={MAX_DIGITS_FOR_SELLING_PRICE}
              id={`selling_price_${index}`}
              name="selling_price"
              value={
                typeof price_variant.selling_price === 'number'
                  ? String(price_variant.selling_price)
                  : price_variant.selling_price
              }
              onChange={handleOnNumberChange('selling_price', index)}
              placeholder="0"
              error={hasError('price_variants', index, 'selling_price')}
              onBlur={handleOnBlur('selling_price', index)}
              size="large"
            />
          </div>
        </div>
        <div className={styles.formColumn}>
          <div className={styles.formItemContainer}>
            <UICarbonNumberField
              label={intl.formatMessage({ id: 'predict.how_many', defaultMessage: 'How many?' })}
              data-testid={`quantity_${index}`}
              autoComplete="off"
              type="number"
              className={styles.isNarrowCarbon}
              id={`quantity_${index}`}
              maxLength={MAX_DIGITS_FOR_QUANTITY}
              name="quantity"
              value={
                typeof price_variant.quantity === 'number' ? String(price_variant.quantity) : price_variant.quantity
              }
              onChange={handleOnNumberChange('quantity', index)}
              placeholder="0"
              error={hasError('price_variants', index, 'quantity')}
              onBlur={handleOnBlur('quantity', index)}
              size="large"
              style={{ marginRight: 16 }}
            />
          </div>
          <div
            className={classNames(styles.formItemContainer, styles.isNarrowCarbon, {
              [styles.disabledCarbonSelect]: index !== 0
            })}
            style={{ marginRight: 16 }}
          >
            <div style={{ height: 29 }} />
            <Select
              aria-label={intl.formatMessage({
                id: 'common.repeat_pattern.aria_label',
                defaultMessage: 'Repeat pattern'
              })}
              data-testid={`repeat_pattern_${index}`}
              autoComplete="off"
              onChange={handleOnChange('repeat_pattern')}
              id={`repeat_pattern_${index}`}
              value={price_variant.repeat_pattern}
              disabled={index !== 0}
              size="large"
            >
              <Option text={intl.formatMessage({ id: 'common.weekly', defaultMessage: 'Weekly' })} value="weekly" />
              <Option text={intl.formatMessage({ id: 'common.monthly', defaultMessage: 'Monthly' })} value="monthly" />
              <Option
                text={intl.formatMessage({ id: 'common.quarterly', defaultMessage: 'Quarterly' })}
                value="quarterly"
              />
              <Option
                text={intl.formatMessage({ id: 'common.annually', defaultMessage: 'Annually' })}
                value="annually"
              />
            </Select>
          </div>
        </div>
        <div className={clsx(styles.formColumn, styles.formSpacedColumn)}>
          <div className={styles.formItemContainer}>
            <DateInput
              label={intl.formatMessage({ id: 'common.start_date', defaultMessage: 'Start date' })}
              data-testid={`start_date_${index}`}
              id={`start_date_${index}`}
              onChange={handleCarbonStartDateChange}
              value={intl.formatDate(price_variant.start_date, { day: '2-digit', month: 'short', year: 'numeric' })}
              maxDate={format(MAX_DATE, 'yyyy-MM-dd')}
              minDate={index === 0 ? format(MIN_DATE, 'yyyy-MM-dd') : format(price_variant.start_date, 'yyyy-MM-dd')}
              disabled={index !== 0}
              error={hasError('price_variants', index, 'start_date')}
              disablePortal={false}
              maxWidth="185px"
              size="large"
            />
          </div>
          <div className={styles.formItemContainer}>
            <DateInput
              label={intl.formatMessage({ id: 'common.end_date', defaultMessage: 'End date' })}
              data-testid={`end_date_${index}`}
              id={`end_date_${index}`}
              onChange={handleCarbonEndDateChange}
              value={
                price_variant.end_date
                  ? intl.formatDate(price_variant.end_date, { day: '2-digit', month: 'short', year: 'numeric' })
                  : ''
              }
              maxDate={format(MAX_DATE, 'yyyy-MM-dd')}
              minDate={format(getNextDay(price_variant.start_date), 'yyyy-MM-dd')}
              error={missing_end_date_for_last_variant}
              disablePortal={false}
              maxWidth="185px"
              size="large"
            />
          </div>
        </div>
      </div>
      <div className={styles.formWrapper}>
        <div className={styles.formColumn}>
          <div className={styles.formItemContainer}>
            <UICarbonNumberField
              label={intl.formatMessage({ id: 'predict.monthly_growth_percent', defaultMessage: 'Monthly Growth %' })}
              data-testid={`growth_${index}`}
              autoComplete="off"
              type="percentage"
              clampMax={MAX_PERCENTAGE}
              clampMin={0}
              className={styles.isNarrowCarbon}
              id={`growth_${index}`}
              name="growth"
              value={typeof price_variant.growth === 'number' ? String(price_variant.growth) : price_variant.growth}
              onChange={handleOnNumberChange('growth', index)}
              onBlur={handleOnBlur('growth', index)}
              placeholder="0"
              disabled={index !== 0}
              error={hasError('price_variants', index, 'growth')}
              size="large"
            />
          </div>
        </div>
        <div className={styles.formColumn}>
          <div className={styles.formItemContainer}>
            <UICarbonNumberField
              label={intl.formatMessage({ id: 'predict.monthly_refund_percent', defaultMessage: 'Monthly Refund %' })}
              data-testid={`refund_${index}`}
              autoComplete="off"
              type="percentage"
              clampMax={MAX_PERCENTAGE}
              clampMin={0}
              className={styles.isNarrowCarbon}
              id={`refund_${index}`}
              name="refund"
              value={typeof price_variant.refund === 'number' ? String(price_variant.refund) : price_variant.refund}
              onChange={handleOnNumberChange('refund', index)}
              onBlur={handleOnBlur('refund', index)}
              placeholder="0"
              disabled={index !== 0}
              error={hasError('price_variants', index, 'refund')}
              size="large"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
